import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { getLocations, LocationRow } from '../Api/Locations/locationsApi';
import { accountScopedRead } from './accountScopedRead';
import { addError } from './Error';

const subject = new BehaviorSubject<LocationRow[] | null>(null);
const loadingSubject = new BehaviorSubject<boolean>(false);

const reads = accountScopedRead<LocationRow[] | null, LocationRow[]>({
  subject,
  request: getLocations,
  empty: [],
  toValue: (data) => data,
  onResult: (error) =>
    addError('locations', error ? (error as { message?: string }).message || 'Unable to load locations' : undefined)
});

let latestFetch = 0;

export interface UseLocationsReturn {
  locations: LocationRow[] | null;
  loading: boolean;
  fetchLocations: () => Promise<void>;
  refreshLocations: () => Promise<void>;
}

export const useLocations = (): UseLocationsReturn => {
  const [locations, setLocations] = useState<LocationRow[] | null>(subject.getValue());
  const [loading, setLoading] = useState<boolean>(loadingSubject.getValue());

  useEffect(() => {
    const locationsSub = subject.subscribe(setLocations);
    const loadingSub = loadingSubject.subscribe(setLoading);
    return () => {
      locationsSub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, []);

  const fetchLocations = async (): Promise<void> => {
    const fetchId = ++latestFetch;
    loadingSubject.next(true);
    try {
      await reads.load();
    } finally {
      if (fetchId === latestFetch) loadingSubject.next(false);
    }
  };

  const refreshLocations = (): Promise<void> => reads.refresh();

  return { locations, loading, fetchLocations, refreshLocations };
};
